import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, Plus, Loader2, MapPin, X, CalendarDays, Globe, Download, Bell, CalendarCheck, Edit3, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { schoolApi as api } from '@/components/school/schoolApi';
import SemesterCloseOutDialog from '@/components/school/SemesterCloseOutDialog';

const AcademicYearsPanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showYearForm, setShowYearForm] = useState(false);
  const [showTermForm, setShowTermForm] = useState(false);
  const [campusFilter, setCampusFilter] = useState('all'); // 'all' | 'shared' | <campus_id>
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_current: false, campus_id: '', holidays: [] });
  const [editingYearId, setEditingYearId] = useState(null);
  const [termForm, setTermForm] = useState({ name: '', academic_year_id: '', start_date: '', end_date: '', order: 1, is_current: false, campus_id: '', holidays: [] });

  // Fetch campuses for the tenant — drives the campus selector
  const { data: campusesData } = useQuery({
    queryKey: ['school-campuses', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/campuses`).catch(() => null),
    enabled: !!tenantId,
  });
  const campuses = Array.isArray(campusesData) ? campusesData : (campusesData?.items || []);

  const campusName = (id) => {
    if (!id) return 'All campuses (shared)';
    const c = campuses.find(x => x.id === id);
    return c ? c.name : id.slice(-6);
  };

  const queryParam = (campusFilter === 'all') ? '' : `?campus_id=${campusFilter}`;

  const { data: years = [], isLoading: yearsLoading } = useQuery({
    queryKey: ['school-academic-years', tenantId, campusFilter],
    queryFn: () => api(`/tenants/${tenantId}/academic-years${queryParam}`),
    enabled: !!tenantId,
  });

  const [cloneTarget, setCloneTarget] = useState(null);
  const [closeoutTerm, setCloseoutTerm] = useState(null);
  const [cloneSource, setCloneSource] = useState('');
  const [cloneOpts, setCloneOpts] = useState({ timetable: true, fees: true, classes: false });

  const cloneMutation = useMutation({
    mutationFn: ({ targetId, sourceId, opts }) => api(
      `/tenants/${tenantId}/terms/${targetId}/clone-from/${sourceId}`
      + `?include_timetable=${!!opts.timetable}&include_fee_structures=${!!opts.fees}`
      + `&include_classes=${!!opts.classes}`,
      { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(
        `Copied into ${r.target.name}: ${r.timetable.copied} timetable period(s), `
        + `${r.fee_structures.copied} fee structure(s), ${r.classes.copied} class(es)`
        + (r.totals.skipped ? ` · ${r.totals.skipped} already there` : ''));
      setCloneTarget(null);
      queryClient.invalidateQueries({ queryKey: ['school-academic-structure'] });
      queryClient.invalidateQueries({ queryKey: ['school-timetable-weekly'] });
      queryClient.invalidateQueries({ queryKey: ['fee-structures'] });
    },
    onError: (e) => toast.error(e?.message || 'Copy failed'),
  });

  const deleteTermMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/terms/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Semester deleted');
      queryClient.invalidateQueries({ queryKey: ['school-terms'] });
      queryClient.invalidateQueries({ queryKey: ['school-academic-structure'] });
    },
    onError: (e) => toast.error(e?.message || 'Could not delete semester'),
  });

  const { data: terms = [] } = useQuery({
    queryKey: ['school-terms', tenantId, campusFilter],
    queryFn: () => api(`/tenants/${tenantId}/terms${queryParam}`),
    enabled: !!tenantId,
  });

  const createYearMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/academic-years`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('Academic year created');
      setShowYearForm(false);
      setEditingYearId(null);
      setYearForm({ name: '', start_date: '', end_date: '', is_current: false, campus_id: '', holidays: [] });
      queryClient.invalidateQueries({ queryKey: ['school-academic-years'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateYearMutation = useMutation({
    mutationFn: ({ id, data }) => api(`/tenants/${tenantId}/academic-years/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('Academic year updated');
      setShowYearForm(false);
      setEditingYearId(null);
      setYearForm({ name: '', start_date: '', end_date: '', is_current: false, campus_id: '', holidays: [] });
      queryClient.invalidateQueries({ queryKey: ['school-academic-years'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteYearMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/academic-years/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Academic year deleted');
      queryClient.invalidateQueries({ queryKey: ['school-academic-years'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const createTermMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/terms`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('Term created');
      setShowTermForm(false);
      setTermForm({ name: '', academic_year_id: '', start_date: '', end_date: '', order: 1, is_current: false, campus_id: '', holidays: [] });
      queryClient.invalidateQueries({ queryKey: ['school-terms'] });
    },
    onError: (e) => toast.error(e.message),
  });

  // Helpers for the holidays editor inside the dialogs
  const addHoliday = (formSetter, form) => {
    formSetter({ ...form, holidays: [...(form.holidays || []), { name: '', date: '', end_date: '' }] });
  };
  const removeHoliday = (formSetter, form, idx) => {
    const next = (form.holidays || []).filter((_, i) => i !== idx);
    formSetter({ ...form, holidays: next });
  };
  const updateHoliday = (formSetter, form, idx, patch) => {
    const next = (form.holidays || []).map((h, i) => (i === idx ? { ...h, ...patch } : h));
    formSetter({ ...form, holidays: next });
  };

  // Auto-import public holidays from a country API (free Nager.Date)
  const [importing, setImporting] = useState(false);
  const importHolidays = async (formSetter, form, country, year) => {
    if (!country || !year) {
      toast.error('Pick a country and a year first');
      return;
    }
    setImporting(true);
    try {
      const res = await api(`/holidays/lookup?country=${country}&year=${year}`);
      if (!res.ok) {
        toast.error(res.error || 'Could not fetch holidays');
        return;
      }
      // De-dupe by (name + date)
      const existingKeys = new Set((form.holidays || []).map(h => `${h.name}|${h.date}`));
      const merged = [...(form.holidays || [])];
      let added = 0;
      for (const h of res.holidays) {
        const k = `${h.name}|${h.date}`;
        if (existingKeys.has(k)) continue;
        merged.push(h);
        existingKeys.add(k);
        added++;
      }
      formSetter({ ...form, holidays: merged });
      toast.success(`Imported ${added} ${country} ${year} holiday${added === 1 ? '' : 's'} (skipped ${res.count - added} duplicates)`);
    } catch (e) {
      toast.error(e.message || 'Holiday import failed');
    } finally {
      setImporting(false);
    }
  };

  // Country pick. Default = main campus country if any.
  const COUNTRY_OPTIONS = [
    { code: 'ZW', name: 'Zimbabwe' }, { code: 'ZA', name: 'South Africa' },
    { code: 'KE', name: 'Kenya' }, { code: 'UG', name: 'Uganda' },
    { code: 'NG', name: 'Nigeria' }, { code: 'GH', name: 'Ghana' },
    { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
  ];
  const [yearImportCountry, setYearImportCountry] = useState('ZW');
  const [termImportCountry, setTermImportCountry] = useState('ZW');

  return (
    <div className="space-y-6" data-testid="academic-years-panel">
      {/* Campus filter (visible only when there are 2+ campuses) */}
      {campuses.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ay-campus-filter">
          <MapPin className="h-4 w-4 text-slate-500" />
          <span className="text-sm text-slate-600">Showing for:</span>
          <Select value={campusFilter} onValueChange={setCampusFilter}>
            <SelectTrigger className="h-8 w-64" data-testid="ay-campus-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campuses (combined view)</SelectItem>
              <SelectItem value="shared">Shared (no specific campus)</SelectItem>
              {campuses.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400">Each campus can have its own years, terms, and holidays.</span>
          <Button
            size="sm"
            variant="outline"
            className="h-8 ml-auto"
            onClick={async () => {
              const cid = (campusFilter === 'all' || campusFilter === 'shared') ? '' : campusFilter;
              try {
                const res = await api(`/tenants/${tenantId}/holidays/send-reminders?campus_id=${cid}&days_ahead=14`, { method: 'POST' });
                if (res.sent === 0 && res.skipped === 0) {
                  toast.message('No upcoming holidays in the next 14 days', { description: 'Nothing to remind staff about.' });
                } else {
                  toast.success(`Sent ${res.sent} reminders (${res.skipped} duplicates skipped)`, {
                    description: res.holidays.map(h => `${h.name} · ${h.date}`).join(' · '),
                  });
                }
              } catch (e) {
                toast.error(e.message || 'Reminder dispatch failed');
              }
            }}
            data-testid="send-holiday-reminders-btn"
          >
            <Bell className="h-3.5 w-3.5 mr-1" />Send 14-day reminders
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={async () => {
              const cid = (campusFilter === 'all' || campusFilter === 'shared') ? '' : campusFilter;
              try {
                const res = await api(`/tenants/${tenantId}/attendance/seed-holidays?campus_id=${cid}&days_back=90&days_forward=30`, { method: 'POST' });
                if (res.created_records === 0 && res.skipped === 0) {
                  toast.message('No holidays in the rolling window', { description: 'Nothing to backfill.' });
                } else {
                  toast.success(`Auto-marked ${res.created_records} students excused on ${res.dates.length} holiday day${res.dates.length === 1 ? '' : 's'}`, {
                    description: res.dates.map(d => `${d.name} · ${d.date}`).join(' · ') + (res.skipped ? ` · (${res.skipped} class-days already had attendance and were left alone)` : ''),
                  });
                }
              } catch (e) {
                toast.error(e.message || 'Auto-mark failed');
              }
            }}
            data-testid="auto-mark-holidays-btn"
          >
            <CalendarCheck className="h-3.5 w-3.5 mr-1" />Auto-mark students excused
          </Button>
        </div>
      )}

      {/* Academic Years */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Academic Years</h2>
          <Button size="sm" onClick={() => setShowYearForm(true)} data-testid="add-academic-year-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Year
          </Button>
        </div>
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Campus</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Holidays</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {yearsLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : years.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No academic years configured</TableCell></TableRow>
              ) : years.map(y => (
                <TableRow key={y.id}>
                  <TableCell className="font-medium">{y.name}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="text-[10px]">{campusName(y.campus_id)}</Badge>
                  </TableCell>
                  <TableCell>{y.start_date}</TableCell>
                  <TableCell>{y.end_date}</TableCell>
                  <TableCell className="text-xs text-slate-500">{(y.holidays || []).length}</TableCell>
                  <TableCell>
                    {y.is_current
                      ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Current</Badge>
                      : <Badge variant="secondary">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => {
                        setYearForm({
                          name: y.name || '', start_date: y.start_date || '', end_date: y.end_date || '',
                          is_current: !!y.is_current, campus_id: y.campus_id || '', holidays: y.holidays || [],
                        });
                        setEditingYearId(y.id);
                        setShowYearForm(true);
                      }}
                      data-testid={`edit-year-${y.id}`}
                    ><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-rose-600"
                      onClick={() => {
                        if (window.confirm(`Delete academic year "${y.name}"? Any linked terms will need to be reassigned.`)) {
                          deleteYearMutation.mutate(y.id);
                        }
                      }}
                      data-testid={`delete-year-${y.id}`}
                    ><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Terms */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Terms / Semesters</h2>
          <Button size="sm" onClick={() => setShowTermForm(true)} disabled={years.length === 0} data-testid="add-term-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Term
          </Button>
        </div>
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Campus</TableHead>
                <TableHead>Academic Year</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>Holidays</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {terms.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No terms configured</TableCell></TableRow>
              ) : terms.map(t => {
                const parentYear = years.find(y => y.id === t.academic_year_id);
                return (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{campusName(t.campus_id)}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{parentYear?.name || '-'}</TableCell>
                    <TableCell>{t.start_date}</TableCell>
                    <TableCell>{t.end_date}</TableCell>
                    <TableCell className="text-xs text-slate-500">{(t.holidays || []).length}</TableCell>
                    <TableCell>{t.order}</TableCell>
                    <TableCell>
                      {t.is_current
                        ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Current</Badge>
                        : <Badge variant="secondary">Not current</Badge>}
                      {(t.status === 'closed' || t.results_locked) && (
                        <Badge variant="outline" className="ml-1 text-[10px] bg-slate-100 text-slate-600"
                               data-testid={`term-closed-${t.id}`}>Closed</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        size="sm" variant="outline" className="h-8 mr-1 text-xs"
                        title="Copy another semester's timetable and fees into this one"
                        data-testid={`copy-setup-${t.id}`}
                        onClick={() => {
                          setCloneTarget(t);
                          const others = terms.filter(x => x.id !== t.id
                            && x.academic_year_id === t.academic_year_id);
                          setCloneSource(others[0]?.id || '');
                        }}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy setup
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-8 mr-1 text-xs"
                        title="Closing checklist — lock results and carry unpaid balances forward"
                        data-testid={`closeout-${t.id}`}
                        onClick={() => setCloseoutTerm(t)}
                      >
                        <Lock className="h-3.5 w-3.5 mr-1" />
                        {(t.status === 'closed' || t.results_locked) ? 'Closed' : 'Close out'}
                      </Button>
                      <Button
                        size="icon" variant="ghost" className="h-8 w-8 text-rose-500"
                        title="Delete this semester (only possible while it is empty)"
                        data-testid={`delete-term-${t.id}`}
                        onClick={() => {
                          if (window.confirm(`Delete semester "${t.name}"? Only allowed while it holds no classes, timetable, fees or exams — existing records are never deleted.`)) {
                            deleteTermMutation.mutate(t.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {closeoutTerm && (
        <SemesterCloseOutDialog
          tenantId={tenantId} term={closeoutTerm} terms={terms} years={years}
          onClose={() => setCloseoutTerm(null)}
        />
      )}

      <Dialog open={!!cloneTarget} onOpenChange={(v) => !v && setCloneTarget(null)}>
        <DialogContent data-testid="copy-setup-dialog">
          <DialogHeader>
            <DialogTitle>Copy setup into {cloneTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Copy from</Label>
              <select
                value={cloneSource}
                onChange={(e) => setCloneSource(e.target.value)}
                data-testid="copy-setup-source"
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">— pick a semester —</option>
                {terms
                  .filter(t => t.id !== cloneTarget?.id
                    && t.academic_year_id === cloneTarget?.academic_year_id)
                  .map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="space-y-2 text-sm">
              {[['timetable', 'Timetable (all periods, lecturers and rooms)'],
                ['fees', 'Fee structures'],
                ['classes', 'Classes']].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox" checked={!!cloneOpts[key]}
                    data-testid={`copy-setup-opt-${key}`}
                    onChange={(e) => setCloneOpts(o => ({ ...o, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Safe to run more than once — anything already in {cloneTarget?.name} is skipped, and the
              source semester is never changed.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneTarget(null)}>Cancel</Button>
            <Button
              disabled={!cloneSource || cloneMutation.isPending}
              data-testid="copy-setup-confirm"
              onClick={() => cloneMutation.mutate({
                targetId: cloneTarget.id, sourceId: cloneSource, opts: cloneOpts })}
            >
              {cloneMutation.isPending ? 'Copying…' : 'Copy setup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Year Dialog */}
      <Dialog open={showYearForm} onOpenChange={(o) => { setShowYearForm(o); if (!o) { setEditingYearId(null); setYearForm({ name: '', start_date: '', end_date: '', is_current: false, campus_id: '', holidays: [] }); } }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingYearId ? 'Edit Academic Year' : 'Add Academic Year'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={yearForm.name} onChange={e => setYearForm({ ...yearForm, name: e.target.value })} placeholder="e.g. 2025-2026" data-testid="year-name-input" />
            </div>
            {campuses.length > 0 && (
              <div className="col-span-2">
                <Label>Campus</Label>
                <Select value={yearForm.campus_id || '__shared__'} onValueChange={v => setYearForm({ ...yearForm, campus_id: v === '__shared__' ? '' : v })}>
                  <SelectTrigger data-testid="year-campus-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__shared__">All campuses (shared)</SelectItem>
                    {campuses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} {c.location ? `· ${c.location}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Choose a campus if this year only applies there (e.g. SA campus runs Jan–Dec, ZIM runs Apr–Mar).</p>
              </div>
            )}
            <div>
              <Label>Start Date *</Label>
              <Input type="date" value={yearForm.start_date} onChange={e => setYearForm({ ...yearForm, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input type="date" value={yearForm.end_date} onChange={e => setYearForm({ ...yearForm, end_date: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={yearForm.is_current} onChange={e => setYearForm({ ...yearForm, is_current: e.target.checked })} id="year-current" />
              <Label htmlFor="year-current">Set as current academic year{yearForm.campus_id ? ' (for this campus)' : ''}</Label>
            </div>
            {/* Holidays editor */}
            <div className="col-span-2 border-t pt-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Holidays / breaks</Label>
                <Button size="sm" variant="outline" onClick={() => addHoliday(setYearForm, yearForm)} data-testid="year-add-holiday-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              {/* Auto-import bar */}
              <div className="rounded-md border bg-slate-50 p-2 flex items-center gap-2 mb-2 flex-wrap" data-testid="year-import-bar">
                <Globe className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                <span className="text-xs text-slate-600 shrink-0">Auto-import public holidays:</span>
                <Select value={yearImportCountry} onValueChange={setYearImportCountry}>
                  <SelectTrigger className="h-7 w-40 text-xs" data-testid="year-import-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7"
                  disabled={importing || !yearForm.start_date}
                  onClick={() => importHolidays(setYearForm, yearForm, yearImportCountry, parseInt((yearForm.start_date || '').slice(0, 4)) || new Date().getFullYear())}
                  data-testid="year-import-holidays-btn"
                >
                  {importing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                  Import {(yearForm.start_date || '').slice(0, 4) || 'year'}
                </Button>
                <span className="text-[10px] text-slate-400">Pulls live from date.nager.at — duplicates auto-skipped.</span>
              </div>
              {(yearForm.holidays || []).length === 0 ? (
                <p className="text-xs text-slate-400">No holidays added yet — useful for public holidays specific to this campus / country.</p>
              ) : (
                <div className="space-y-2">
                  {yearForm.holidays.map((h, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`year-holiday-row-${i}`}>
                      <div className="col-span-5">
                        <Input placeholder="Holiday name" value={h.name || ''} onChange={e => updateHoliday(setYearForm, yearForm, i, { name: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Input type="date" value={h.date || ''} onChange={e => updateHoliday(setYearForm, yearForm, i, { date: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Input type="date" placeholder="End (opt.)" value={h.end_date || ''} onChange={e => updateHoliday(setYearForm, yearForm, i, { end_date: e.target.value })} />
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 col-span-1 text-rose-500" onClick={() => removeHoliday(setYearForm, yearForm, i)} title="Remove">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => {
                if (editingYearId) updateYearMutation.mutate({ id: editingYearId, data: yearForm });
                else createYearMutation.mutate(yearForm);
              }}
              disabled={createYearMutation.isPending || updateYearMutation.isPending || !yearForm.name || !yearForm.start_date || !yearForm.end_date}
              data-testid="submit-year-btn"
            >
              {(createYearMutation.isPending || updateYearMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editingYearId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Term Dialog */}
      <Dialog open={showTermForm} onOpenChange={setShowTermForm}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Term</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Term Name *</Label>
              <Input value={termForm.name} onChange={e => setTermForm({ ...termForm, name: e.target.value })} placeholder="e.g. Term 1" data-testid="term-name-input" />
            </div>
            <div className="col-span-2">
              <Label>Academic Year *</Label>
              <Select value={termForm.academic_year_id} onValueChange={v => {
                const y = years.find(yy => yy.id === v);
                // Auto-inherit the year's campus_id so the term lands in the same scope
                setTermForm({ ...termForm, academic_year_id: v, campus_id: y?.campus_id || termForm.campus_id });
              }}>
                <SelectTrigger><SelectValue placeholder="Select academic year" /></SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y.id} value={y.id}>
                      {y.name}{y.campus_id ? ` · ${campusName(y.campus_id)}` : ' · shared'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {campuses.length > 0 && (
              <div className="col-span-2">
                <Label>Campus</Label>
                <Select value={termForm.campus_id || '__shared__'} onValueChange={v => setTermForm({ ...termForm, campus_id: v === '__shared__' ? '' : v })}>
                  <SelectTrigger data-testid="term-campus-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__shared__">All campuses (shared)</SelectItem>
                    {campuses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} {c.location ? `· ${c.location}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Start Date *</Label>
              <Input type="date" value={termForm.start_date} onChange={e => setTermForm({ ...termForm, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input type="date" value={termForm.end_date} onChange={e => setTermForm({ ...termForm, end_date: e.target.value })} />
            </div>
            <div>
              <Label>Order</Label>
              <Input type="number" value={termForm.order} onChange={e => setTermForm({ ...termForm, order: parseInt(e.target.value) || 1 })} min={1} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" checked={termForm.is_current} onChange={e => setTermForm({ ...termForm, is_current: e.target.checked })} id="term-current" />
              <Label htmlFor="term-current">Current term</Label>
            </div>
            {/* Term-level holidays */}
            <div className="col-span-2 border-t pt-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Mid-term breaks / public holidays in this term</Label>
                <Button size="sm" variant="outline" onClick={() => addHoliday(setTermForm, termForm)} data-testid="term-add-holiday-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              {/* Auto-import bar (term) */}
              <div className="rounded-md border bg-slate-50 p-2 flex items-center gap-2 mb-2 flex-wrap" data-testid="term-import-bar">
                <Globe className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                <span className="text-xs text-slate-600 shrink-0">Auto-import public holidays:</span>
                <Select value={termImportCountry} onValueChange={setTermImportCountry}>
                  <SelectTrigger className="h-7 w-40 text-xs" data-testid="term-import-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7"
                  disabled={importing || !termForm.start_date}
                  onClick={async () => {
                    const yr = parseInt((termForm.start_date || '').slice(0, 4)) || new Date().getFullYear();
                    // For terms we filter the imported list to dates that fall inside the term window
                    const startStr = termForm.start_date;
                    const endStr = termForm.end_date;
                    setImporting(true);
                    try {
                      const res = await api(`/holidays/lookup?country=${termImportCountry}&year=${yr}`);
                      if (!res.ok) { toast.error(res.error || 'Could not fetch holidays'); return; }
                      const inTerm = (res.holidays || []).filter(h => {
                        if (!startStr || !endStr) return true;
                        return h.date >= startStr && h.date <= endStr;
                      });
                      const existingKeys = new Set((termForm.holidays || []).map(h => `${h.name}|${h.date}`));
                      const merged = [...(termForm.holidays || [])];
                      let added = 0;
                      for (const h of inTerm) {
                        const k = `${h.name}|${h.date}`;
                        if (existingKeys.has(k)) continue;
                        merged.push(h);
                        existingKeys.add(k);
                        added++;
                      }
                      setTermForm({ ...termForm, holidays: merged });
                      toast.success(`Imported ${added} ${termImportCountry} holiday${added === 1 ? '' : 's'} that fall inside this term`);
                    } catch (e) {
                      toast.error(e.message || 'Holiday import failed');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  data-testid="term-import-holidays-btn"
                >
                  {importing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                  Import in-term
                </Button>
                <span className="text-[10px] text-slate-400">Imports only holidays that fall within this term's dates.</span>
              </div>
              {(termForm.holidays || []).length === 0 ? (
                <p className="text-xs text-slate-400">No breaks added yet.</p>
              ) : (
                <div className="space-y-2">
                  {termForm.holidays.map((h, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`term-holiday-row-${i}`}>
                      <div className="col-span-5">
                        <Input placeholder="Holiday name" value={h.name || ''} onChange={e => updateHoliday(setTermForm, termForm, i, { name: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Input type="date" value={h.date || ''} onChange={e => updateHoliday(setTermForm, termForm, i, { date: e.target.value })} />
                      </div>
                      <div className="col-span-3">
                        <Input type="date" placeholder="End (opt.)" value={h.end_date || ''} onChange={e => updateHoliday(setTermForm, termForm, i, { end_date: e.target.value })} />
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 col-span-1 text-rose-500" onClick={() => removeHoliday(setTermForm, termForm, i)} title="Remove">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => createTermMutation.mutate(termForm)}
              disabled={createTermMutation.isPending || !termForm.name || !termForm.academic_year_id || !termForm.start_date || !termForm.end_date}
              data-testid="submit-term-btn"
            >
              {createTermMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademicYearsPanel;
